import { useMemo } from "react"
import { IconFacePlusOutline18 } from "nucleo-ui-outline-18"

import type {
	MessageReactionType,
	ReactionGroup,
} from "@productlane/lib/src/message-reactions"
import {
	AUTOMATIC_REACTOR_KEY,
	reactionGlyph,
} from "@productlane/lib/src/message-reactions"
import { Button, IconButton, Tooltip } from "@productlane/ui"

import { EmojiGlyph, EmojiPicker } from "@/components/emoji-picker"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { recordEmojiUse } from "@/lib/frequent-emoji"
import { useRole } from "@/lib/useRole"
import { useAuthData } from "@/lib/zero"
import {
	REACTION_COPY,
	useMessageReactions,
	useToggleReaction,
} from "../../../hooks/useMessageReactions"

const MAX_NAMES = 8

/** Names behind a chip, in the order they reacted; the same person reacting from Slack or Linear and Productlane counts once. */
export function reactorNames(
	group: ReactionGroup,
	memberNames: ReadonlyMap<string, string>,
): string {
	const names: string[] = []
	for (const reactor of group.reactors) {
		const name =
			reactor.reactorKey === AUTOMATIC_REACTOR_KEY
				? REACTION_COPY.automatic
				: ((reactor.userId && memberNames.get(reactor.userId)) ??
					reactor.reactorName ??
					REACTION_COPY.someone)
		if (!names.includes(name)) names.push(name)
	}
	const shown = names.slice(0, MAX_NAMES).join(", ")
	const hidden = names.length - MAX_NAMES
	return hidden > 0 ? `${shown} +${hidden}` : shown
}

function useMemberNames() {
	const members = api.workspace.members.useQuery(undefined, {
		cacheTime: Infinity,
		staleTime: Infinity,
	})
	return useMemo(() => {
		const names = new Map<string, string>()
		for (const membership of members.data ?? []) {
			const user = membership.user
			const name = user.name?.trim() || user.email
			if (name) names.set(user.id, name)
		}
		return names
	}, [members.data])
}

/** Chips under a message, like Slack's, with an add button at the end. Clicking a chip adds or takes back your reaction. */
export function MessageReactions({
	threadId,
	messageType,
	messageId,
}: {
	threadId: string
	messageType: MessageReactionType
	messageId: string
}) {
	const groups = useMessageReactions(threadId, messageId)
	const toggle = useToggleReaction(threadId, messageType, messageId)
	const memberNames = useMemberNames()
	const { userId } = useAuthData()
	const { role } = useRole()
	const isViewer = role === "VIEWER"

	if (groups.length === 0) return null

	return (
		<div
			className="flex flex-wrap items-center gap-1"
			data-testid="message-reactions"
		>
			{groups.map((group) => (
				<Tooltip
					key={group.emoji}
					label={
						<div className="max-w-60">
							<div>{reactorNames(group, memberNames)}</div>
							<div className="text-tertiary">:{group.emoji}:</div>
						</div>
					}
				>
					<Button
						variant={group.reactedByMe ? "reaction-selected" : "reaction"}
						className="h-6 min-h-6 px-2 text-xs font-medium tabular-nums"
						// Button wraps its children in its own flex row; the gap belongs there.
						wrapperClassName="gap-1.5"
						aria-pressed={group.reactedByMe}
						aria-disabled={isViewer}
						onClick={() => {
							if (isViewer) {
								toast.info({ title: REACTION_COPY.viewer })
								return
							}
							const emoji = { name: group.emoji, imageUrl: group.imageUrl }
							if (!group.reactedByMe) recordEmojiUse(userId, emoji)
							void toggle(emoji)
						}}
					>
						<EmojiGlyph
							glyph={reactionGlyph(group.emoji) ?? null}
							imageUrl={group.imageUrl}
							name={group.emoji}
						/>
						<span>{group.count}</span>
					</Button>
				</Tooltip>
			))}
			<RoleCheck not={["VIEWER"]}>
				<EmojiPicker
					onSelect={(emoji) => void toggle(emoji)}
					align="start"
					tooltip={REACTION_COPY.add}
				>
					<IconButton
						Icon={IconFacePlusOutline18}
						variant="reaction-add"
						size="md"
						aria-label={REACTION_COPY.add}
						data-testid="add-reaction-chip"
					/>
				</EmojiPicker>
			</RoleCheck>
		</div>
	)
}

/** Opens the picker from the message header; fades in over rows that declare `group/reactable`. */
export function AddReactionButton({
	threadId,
	messageType,
	messageId,
}: {
	threadId: string
	messageType: MessageReactionType
	messageId: string
}) {
	const toggle = useToggleReaction(threadId, messageType, messageId)
	return (
		<RoleCheck not={["VIEWER"]}>
			<div className="flex items-center opacity-0 focus-within:opacity-100 group-hover/reactable:opacity-100 has-[[data-state=open]]:opacity-100">
				<EmojiPicker
					onSelect={(emoji) => void toggle(emoji)}
					tooltip={REACTION_COPY.add}
				>
					<IconButton
						Icon={IconFacePlusOutline18}
						variant="reaction-add"
						size="sm"
						aria-label={REACTION_COPY.add}
						data-testid="add-reaction-button"
					/>
				</EmojiPicker>
			</div>
		</RoleCheck>
	)
}
